import { IGeogridData, IAASHTOResults, IPavedCalculatedDesignThickness } from '../api/IApiResponse';
import { GEOGRID_CONFIG } from '../config/GeogridConfig';

/**
 * @file PavedGeogridMappingEnhancer.ts
 * @description Simplified mapping service for paved projects using centralized AASHTO results.
 * Takes the base mapping from GeoGridDataMapper and enhances it with calculated thickness values
 * and geogrid selections from the centralized AASHTO results (single source of truth).
 */

/**
 * Helper function to check if a field should be mapped or preserved
 * @param currentValue - The current value in the target object
 * @param preserveUserValues - Whether to preserve existing user values
 * @returns true if the field should be mapped, false if it should be preserved
 */
const shouldMapField = (currentValue: any, preserveUserValues: boolean): boolean => {
    if (!preserveUserValues) return true;
    return currentValue === undefined || currentValue === null;
};

/**
 * Enhances the basic paved project mapping with calculated thickness results from the selected design.
 * Uses centralized AASHTO results as the single source of truth for geogrid selections.
 *
 * @param baseGeogridData - The base geogrid data from GeoGridDataMapper
 * @param calculatedDesignThickness - The calculated thickness values from the selected design
 * @param preserveUserValues - Whether to preserve existing user values
 * @param aashtoResults - AASHTO results containing centralized geogrid material selections
 * @returns Enhanced geogrid data with calculated thickness values and centralized geogrid selections
 */
export const enhancePavedGeogridMappingWithCalculatedResults = (
    baseGeogridData: IGeogridData,
    calculatedDesignThickness: IPavedCalculatedDesignThickness | undefined,
    preserveUserValues = false,
    aashtoResults?: IAASHTOResults
): IGeogridData => {
    if (!calculatedDesignThickness) {
        return baseGeogridData;
    }

    // Create a deep copy to avoid mutating the original data
    const enhancedGeogridData: IGeogridData = JSON.parse(JSON.stringify(baseGeogridData));

    const { thickness } = calculatedDesignThickness;

    // Extract geogrid material names and positions from AASHTO results (centralized single source of truth)
    const aashtoBaseGeogridMaterial = aashtoResults?.detailedCalculations?.softwareDesign?.base?.geogridMaterialName;
    const aashtoSubbaseGeogridMaterial = aashtoResults?.detailedCalculations?.softwareDesign?.subbase?.geogridMaterialName;
    const aashtoBaseGeogridPosition = aashtoResults?.detailedCalculations?.softwareDesign?.base?.geogridPosition;
    const aashtoSubbaseGeogridPosition = aashtoResults?.detailedCalculations?.softwareDesign?.subbase?.geogridPosition;

    // Enhance surface layer thickness with calculated values from selected design
    if (thickness.surface !== undefined && thickness.surface > 0) {
        if (!enhancedGeogridData.surfaceLayer) {
            enhancedGeogridData.surfaceLayer = {};
        }
        if (shouldMapField(enhancedGeogridData.surfaceLayer.thickness, preserveUserValues)) {
            enhancedGeogridData.surfaceLayer.thickness = thickness.surface;
        }
    }

    // Enhance base course thickness with calculated values from selected design
    if (thickness.base !== undefined && thickness.base > 0) {
        if (!enhancedGeogridData.geogridBaseCourse) {
            enhancedGeogridData.geogridBaseCourse = {};
        }
        if (shouldMapField(enhancedGeogridData.geogridBaseCourse.thickness, preserveUserValues)) {
            enhancedGeogridData.geogridBaseCourse.thickness = thickness.base;
        }

        // SIMPLIFIED CENTRALIZED GEOGRID SELECTION - AASHTO RESULTS AS SINGLE SOURCE OF TRUTH
        // Now that AASHTO results are populated by CalculationServiceFacade, use them directly
        const finalBaseGeogridMaterial = aashtoBaseGeogridMaterial;
        const finalBaseGeogridPosition = aashtoBaseGeogridPosition;
        let baseGeogridDisplayInfo: { type: string; selectedGeogrid: string; position?: number } | undefined;

        if (finalBaseGeogridMaterial && finalBaseGeogridMaterial !== 'None') {
            // Use AASHTO selection (centralized single source of truth)
            baseGeogridDisplayInfo = {
                type: 'MacGrid EG', // Default type for AASHTO selections
                selectedGeogrid: finalBaseGeogridMaterial,
                position: finalBaseGeogridPosition ?? thickness.base * GEOGRID_CONFIG.FALLBACK_POSITION_PERCENTAGE, // Use centralized position or fallback
            };
        } else {
        }

        if (finalBaseGeogridMaterial && finalBaseGeogridMaterial !== 'None' && baseGeogridDisplayInfo) {
            if (!enhancedGeogridData.geogridBaseCourse.geogrids) {
                enhancedGeogridData.geogridBaseCourse.geogrids = [];
            }

            // Check if there's already a geogrid entry - if not, create one with the selected geogrid
            if (enhancedGeogridData.geogridBaseCourse.geogrids.length === 0) {
                // Create a new geogrid entry with the selected material and centralized position
                enhancedGeogridData.geogridBaseCourse.geogrids.push({
                    position: baseGeogridDisplayInfo.position!, // Use centralized position
                    selectedGeogrid: baseGeogridDisplayInfo.selectedGeogrid,
                    type: baseGeogridDisplayInfo.type,
                    designStrain: 1, // Default design strain
                });
            } else if (!preserveUserValues) {
                // If geogrid exists but we're not preserving user values, update the selection
                enhancedGeogridData.geogridBaseCourse.geogrids[0].selectedGeogrid = baseGeogridDisplayInfo.selectedGeogrid;
                enhancedGeogridData.geogridBaseCourse.geogrids[0].type = baseGeogridDisplayInfo.type;
                enhancedGeogridData.geogridBaseCourse.geogrids[0].position = baseGeogridDisplayInfo.position!; // Use centralized position
            }
        } else {
            // No geogrid selection from either AASHTO or paved form - preserve existing user choice or leave empty
        }
    }

    // Enhance subbase course thickness with calculated values from selected design
    if (thickness.subbase !== undefined && thickness.subbase > 0) {
        if (!enhancedGeogridData.geogridSubBaseCourse) {
            enhancedGeogridData.geogridSubBaseCourse = {};
        }
        if (shouldMapField(enhancedGeogridData.geogridSubBaseCourse.thickness, preserveUserValues)) {
            enhancedGeogridData.geogridSubBaseCourse.thickness = thickness.subbase;
        }

        // SIMPLIFIED CENTRALIZED SUBBASE GEOGRID SELECTION - AASHTO RESULTS AS SINGLE SOURCE OF TRUTH
        // Now that AASHTO results are populated by CalculationServiceFacade, use them directly
        const finalSubbaseGeogridMaterial = aashtoSubbaseGeogridMaterial;
        const finalSubbaseGeogridPosition = aashtoSubbaseGeogridPosition;
        let subbaseGeogridDisplayInfo: { type: string; selectedGeogrid: string; position?: number } | undefined;

        if (finalSubbaseGeogridMaterial && finalSubbaseGeogridMaterial !== 'None') {
            // Use AASHTO selection (centralized single source of truth)
            subbaseGeogridDisplayInfo = {
                type: 'MacGrid EG', // Default type for AASHTO selections
                selectedGeogrid: finalSubbaseGeogridMaterial,
                position: finalSubbaseGeogridPosition ?? thickness.subbase * GEOGRID_CONFIG.FALLBACK_POSITION_PERCENTAGE, // Use centralized position or fallback
            };
        } else {
        }

        if (finalSubbaseGeogridMaterial && finalSubbaseGeogridMaterial !== 'None' && subbaseGeogridDisplayInfo) {
            if (!enhancedGeogridData.geogridSubBaseCourse.geogrids) {
                enhancedGeogridData.geogridSubBaseCourse.geogrids = [];
            }

            // Check if there's already a geogrid entry - if not, create one with the selected geogrid
            if (enhancedGeogridData.geogridSubBaseCourse.geogrids.length === 0) {
                // Create a new geogrid entry with the selected material and centralized position
                enhancedGeogridData.geogridSubBaseCourse.geogrids.push({
                    position: subbaseGeogridDisplayInfo.position!, // Use centralized position
                    selectedGeogrid: subbaseGeogridDisplayInfo.selectedGeogrid,
                    type: subbaseGeogridDisplayInfo.type,
                    designStrain: 1, // Default design strain
                });
            } else if (!preserveUserValues) {
                // If geogrid exists but we're not preserving user values, update the selection
                enhancedGeogridData.geogridSubBaseCourse.geogrids[0].selectedGeogrid = subbaseGeogridDisplayInfo.selectedGeogrid;
                enhancedGeogridData.geogridSubBaseCourse.geogrids[0].type = subbaseGeogridDisplayInfo.type;
                enhancedGeogridData.geogridSubBaseCourse.geogrids[0].position = subbaseGeogridDisplayInfo.position!; // Use centralized position
            }
        } else {
            // No geogrid selection from centralized AASHTO results - user will make selection
        }
    }

    return enhancedGeogridData;
};

/**
 * Creates a comprehensive mapping for paved projects by combining base mapping with calculated design results.
 * This is a convenience function that uses centralized AASHTO results as the single source of truth.
 *
 * @param baseGeogridData - The base geogrid data from GeoGridDataMapper
 * @param calculatedDesignThickness - The calculated design thickness from Results_Paved
 * @param preserveUserValues - Whether to preserve existing user values
 * @param aashtoResults - AASHTO results containing centralized geogrid material selections
 * @returns Complete enhanced geogrid mapping for paved projects
 */
export const createComprehensivePavedGeogridMapping = (
    baseGeogridData: IGeogridData,
    calculatedDesignThickness: IPavedCalculatedDesignThickness | undefined,
    preserveUserValues = false,
    aashtoResults?: IAASHTOResults
): IGeogridData => {
    // Start with the base mapping
    let result = baseGeogridData;

    // Enhance with calculated design results if available
    if (calculatedDesignThickness) {
        result = enhancePavedGeogridMappingWithCalculatedResults(result, calculatedDesignThickness, preserveUserValues, aashtoResults);
    }

    return result;
};
